import { useRef, useEffect, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { NikoProjectExtended } from '../../types/project';

gsap.registerPlugin(ScrollTrigger);

interface ProjectResultsProps {
  project: NikoProjectExtended;
}

interface AnimatedCounterProps {
  value: string;
  trigger: boolean;
}

function AnimatedCounter({ value, trigger }: AnimatedCounterProps) {
  const [displayValue, setDisplayValue] = useState(value);
  const counterRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!trigger || !counterRef.current) return;

    // Extract number and suffix (e.g., "+35%" -> { num: 35, prefix: "+", suffix: "%" })
    const match = value.match(/^([+-]?)(\d+)(.*)$/);
    if (!match) {
      setDisplayValue(value);
      return;
    }

    const prefix = match[1] ?? '';
    const numStr = match[2] ?? '0';
    const suffix = match[3] ?? '';
    const targetNum = parseInt(numStr, 10);

    const obj = { val: 0 };
    gsap.to(obj, {
      val: targetNum,
      duration: 1.5,
      ease: 'power2.out',
      onUpdate: () => {
        setDisplayValue(`${prefix}${Math.round(obj.val)}${suffix}`);
      },
    });
  }, [trigger, value]);

  return <span ref={counterRef}>{displayValue}</span>;
}

export function ProjectResults({ project }: ProjectResultsProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    let ctx: gsap.Context;

    const timer = setTimeout(() => {
      ctx = gsap.context(() => {
        ScrollTrigger.create({
          trigger: sectionRef.current,
          start: 'top 80%',
          onEnter: () => setIsVisible(true),
        });

        gsap.fromTo(
          '.result-card',
          { y: 40, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
            stagger: 0.15,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 80%',
            },
          }
        );
        ScrollTrigger.refresh();
      }, sectionRef);
    }, 150);

    return () => {
      clearTimeout(timer);
      if (ctx) ctx.revert();
    };
  }, []);

  if (!project.results || project.results.length === 0) {
    return null;
  }

  return (
    <section ref={sectionRef} className="py-24 px-8 md:px-12 lg:px-24 bg-emerald-500/5">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-xs text-white/30 tracking-[0.3em] uppercase mb-12">
          Ergebnisse
        </h2>

        <div className="grid md:grid-cols-3 gap-8">
          {project.results.map((result, index) => (
            <div
              key={index}
              className="result-card text-center p-8 border border-emerald-500/20 rounded-xl bg-emerald-500/5"
            >
              {/* Metric Value */}
              <div className="text-5xl md:text-6xl font-light text-emerald-500 mb-4">
                <AnimatedCounter value={result.value} trigger={isVisible} />
              </div>

              {/* Metric Name */}
              <h3 className="text-lg text-white font-medium mb-2">
                {result.metric}
              </h3>

              {/* Description */}
              {result.description && (
                <p className="text-sm text-white/40">{result.description}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
